import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { HistoryChangeKind, HistoryTimelineEvent } from '../../models/history-timeline.modal';

// "1 test", "3 tests": used in the change tooltips
export function plural(count: number, singular: string, pluralForm = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : pluralForm}`;
}

const CHANGE_ICONS: Record<HistoryChangeKind, string> = {
  added: 'add_circle',
  removed: 'remove_circle',
  edited: 'edit',
  assigned: 'person',
};

/**
 * Horizontal timeline of history actions, newest on the left. The < > buttons replace the scrollbar.
 * The page using it shows the selected action's details below it (e.g. the worksheet history pages).
 */
@Component({
    selector: 'app-history-timeline',
    templateUrl: './history-timeline.component.html',
    styleUrls: ['./history-timeline.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class HistoryTimelineComponent implements OnChanges {
  // Newest first: the most recent action is the leftmost point on the timeline
  @Input() events: HistoryTimelineEvent[] = [];
  @Input() selectedIndex = -1;
  @Output() selectedIndexChange = new EventEmitter<number>();
  // Each arrow is disabled at its end
  canScrollLeft = false;
  canScrollRight = false;
  @ViewChild('timelineTrack') timelineTrack?: ElementRef<HTMLElement>;

  ngOnChanges(): void {
    // Once rendered, work out which arrows apply
    setTimeout(() => this.updateScrollButtons());
  }

  changeIcon(kind: HistoryChangeKind): string {
    return CHANGE_ICONS[kind];
  }

  selectEvent(index: number): void {
    this.selectedIndex = index;
    this.selectedIndexChange.emit(index);
  }

  // Moves the timeline by most of its visible width so a few points stay in view for context
  scrollTimeline(direction: -1 | 1): void {
    const track = this.timelineTrack?.nativeElement;
    track?.scrollBy({ left: direction * track.clientWidth * 0.8, behavior: 'smooth' });
  }

  @HostListener('window:resize')
  updateScrollButtons(): void {
    const track = this.timelineTrack?.nativeElement;
    if (!track) {
      return;
    }
    this.canScrollLeft = track.scrollLeft > 0;
    this.canScrollRight = track.scrollLeft + track.clientWidth < track.scrollWidth - 1;
  }

  get hasOverflow(): boolean {
    return this.canScrollLeft || this.canScrollRight;
  }
}
